/**
 * The mat. One stitched ring bounds the trick and seats the players; the
 * callout is a sentence on the felt, not a banner. Nothing here has a box.
 */
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react'
import { FaceDownStack, SeatFan, TrickCard } from './Card'
import type { Position, TableView } from './view'
import { useSettings } from '../settings/settings'
import { useStillMs, waitingWord } from './waiting'
import { BotIcon } from './atoms'
import { useNudge } from './touch'
import { RemoteHands } from './RemoteHands'
import { FeltLight } from './FeltLight'
import { holdTilt } from './tilt'

/** The slant a card thrown from each seat can land at, in degrees.
 *
 *  Trickster gives every seat a range and draws inside it on each play, so a
 *  trick is a scatter rather than four cards in the same four slots; the ranges
 *  here keep the composition the fixed rotations had and only put play in it.
 *  Theirs are far wider (the side seats land near 45°) because their side hands
 *  are rotated bodily; this trick box is a third the size, so the spread is
 *  narrower or the cards leave it. */
const TILT: Record<Position, readonly [number, number]> = {
  n: [-8, 0],
  w: [-14, -4],
  e: [3, 13],
  s: [-1, 7],
}

/** Where the cards go when the trick is swept up: off towards whoever took it.
 *  The viewer's own seat is below the felt, so a trick they win comes down out
 *  of the ring rather than across it. */
const SWEEP: Record<Position, readonly [number, number]> = {
  n: [0, -42],
  w: [-46, -12],
  e: [46, -12],
  s: [0, 40],
}

/** A stable number in [0, 1) for a card at a seat.
 *
 *  Trickster calls `Math.random` once per played card and pins the result on
 *  the view, which is enough for one client but means a reconnect re-scatters
 *  the table and no two players ever see the same one. Hashing the card and the
 *  seat costs the same, survives a remount, and makes the scatter part of the
 *  hand everyone is looking at. FNV-1a, because it only has to be even. */
function jitter(seed: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return ((h >>> 0) % 1024) / 1024
}

function tiltFor(position: Position, card: string, seat: number): number {
  const [lo, hi] = TILT[position]
  return lo + jitter(`${card}:${seat}`) * (hi - lo)
}

export function Felt({
  view,
  gather,
  children,
}: {
  view: TableView
  /** The sweep is under way: losers gather under the winner, then all four go. */
  gather?: boolean
  children?: ReactNode
}): JSX.Element {
  // The table is up: let it answer the phone (tilt.ts reads the sensor only
  // while something with cards on it is on screen).
  const felt = useRef<HTMLElement | null>(null)
  useEffect(() => holdTilt(felt.current), [])
  return (
    <section className="felt play-felt" ref={felt}>
      <FeltLight view={view} />
      <div className="ring">
        {(['n', 'w', 'e'] as const).map((position) => {
          const seat = view.at(position)
          return seat ? <SeatLabel key={position} view={view} position={position} /> : null
        })}
        <TrickArea view={view} gather={gather} />
        <LeadWait view={view} />
        <div className="callout trick-callout">{view.gameUi.callout(view)}</div>
        <RemoteHands view={view} />
        {children}
      </div>
    </section>
  )
}

export function SeatLabel({ view, position }: { view: TableView; position: Position }): JSX.Element | null {
  const [settings] = useSettings()
  // Hooks run before the early return: a seat that is absent on this snapshot
  // may be present on the next, and bailing out first would change the order.
  const stillMs = useStillMs(view.snapshot.seq)
  const seat = view.at(position)
  if (!seat) return null
  const status = view.gameUi.seatStatus(view, seat.seat)
  // Only ever said about the seat everyone is waiting on, and only once the
  // game has nothing of its own to say about them — a contract or a trick
  // count is more useful than a clock.
  const waiting = seat.toAct && !status ? waitingWord(stillMs) : null
  const won = view.trick?.winner === seat.seat
  // The contract stays pinned to the seat that won it, the way Trickster keeps
  // the winning bid on the contractor's name tag for the whole hand: whose
  // hand this is should not live only in the corner of the status band.
  const contract = view.gameUi.auctionStrip(view)[seat.seat]
  // Trickster's rule, in both the auction and the play: while somebody is on
  // the clock every other seat stands back, and while nobody is (the trick
  // resting, the hand ending) every seat is lit.
  const lit = seat.toAct || view.toAct === null
  const classes = ['seat', `seat-${position}`, seat.toAct ? 'turn' : '', lit ? '' : 'waiting', !seat.active ? 'out' : '']
    .filter(Boolean)
    .join(' ')
  return (
    <div className={classes}>
      {/* The name plate. On the side seats it is turned to run along the edge
          of the table, as Trickster turns its left and right players. */}
      <div className="seat-label">
        <div className="seat-head">
          <div className="seat-name">
            {seat.isBot ? <BotIcon /> : null}
            {seat.name}
          </div>
          {won ? <span className="delta delta-inline">+1</span> : null}
          {contract?.high ? <span className="bid-chip bid on-brass small">{contract.label}</span> : null}
        </div>
        <div className={waiting ? 'seat-sub waiting' : 'seat-sub'}>{status ?? waiting ?? ' '}</div>
      </div>
      {/* The wire already draws the distinction, and means it: `count` is "an
          opponent's hand", `faceDown` is "Misère partner sitting out — rendered
          as a stack, never as fanned backs" (shared/core/visible.ts). So the
          kind picks the rendering, not anything this component works out.

          `count` used to render nothing at all, which is why three players'
          hands were invisible and the felt read as captions round a trick. */}
      {seat.holding?.kind === 'count' && seat.holding.count > 0 ? (
        <SeatFan count={seat.holding.count} position={position} lit={lit} deck={settings.deck} />
      ) : null}
      {seat.holding?.kind === 'faceDown' && seat.holding.count > 0 ? (
        <FaceDownStack count={seat.holding.count} deck={settings.deck} />
      ) : null}
    </div>
  )
}

/** Who is to lead, in the middle of the table while it is empty — Trickster's
 *  "Your turn to lead", and its "Waiting for *name*" for everyone else. Once a
 *  card is down the trick itself and the callout under it do the talking. */
function LeadWait({ view }: { view: TableView }): JSX.Element | null {
  const trick = view.trick
  const pending = view.hand?.pending ?? null
  // The contractor exchanging the kitty: the trick is empty, but nobody is
  // about to lead it yet, and saying they were was half of what made the end
  // of the auction confusing.
  if (view.hand?.phase === 'decision' && pending) {
    return (
      <div className="table-wait" role="status">
        <div className="table-wait-head">
          Waiting for <b>{view.name(pending.seat)}</b> {view.gameUi.decisionWaiting(pending.kind)}
        </div>
      </div>
    )
  }
  if (!trick || trick.plays.length > 0 || view.toAct === null) return null
  return (
    <div className="table-wait" role="status">
      <div className="table-wait-head">
        {view.isMyTurn ? (
          <b>Your turn to lead</b>
        ) : (
          <>
            Waiting for <b>{view.name(view.toAct)}</b> to lead
          </>
        )}
      </div>
    </div>
  )
}

export function TrickArea({ view, gather }: { view: TableView; gather?: boolean }): JSX.Element {
  const [settings] = useSettings()
  const box = useRef<HTMLDivElement | null>(null)
  useNudge(box, settings.flick)
  const trick = view.trick
  if (!trick) return <div className="trick" ref={box} />
  // The winner is the server's word once the trick is complete; mid-trick only
  // the game module knows its own ranking, so the GameUi is asked.
  const best = view.gameUi.winningSeat(view)
  // The sweep goes to whoever is taking it. Before anyone is — an unfinished
  // trick is never swept — it stays put, which is also what the CSS defaults to.
  const to = best === null ? null : (view.seats[best]?.position ?? null)
  const sweep = to ? SWEEP[to] : null
  const fresh = freshPlays(view)
  return (
    <div
      ref={box}
      className={gather ? 'trick gather' : 'trick'}
      style={
        sweep ? ({ '--sweep-x': `${sweep[0]}px`, '--sweep-y': `${sweep[1]}px` } as CSSProperties) : undefined
      }
    >
      {/* Where your card would land, for the drop cue while one is being
          carried (touch.ts). Only while your own place is still empty. */}
      {view.isMyTurn && view.mySeat !== null && !trick.plays.some((p) => p.seat === view.mySeat) ? (
        <div className="drop-slot" aria-hidden="true">
          <span>Let go to play</span>
        </div>
      ) : null}
      {trick.plays.map((play) => {
        const seat = view.seats[play.seat]
        if (!seat) return null
        return (
          <TrickCard
            key={play.seat}
            card={play.card}
            position={seat.position}
            winning={best === play.seat}
            tilt={tiltFor(seat.position, play.card, play.seat)}
            fresh={fresh.has(play.seat)}
            seat={play.seat}
            deck={settings.deck}
          />
        )
      })}
    </div>
  )
}

/** The seats whose card was played in this snapshot. Events are advisory and
 *  exist so the UI can animate what changed (core/events.ts); a card that was
 *  already lying there when the table mounted did not just arrive. */
function freshPlays(view: TableView): ReadonlySet<number> {
  const out = new Set<number>()
  for (const tagged of view.snapshot.events) {
    if (tagged.event.type === 'played') out.add(tagged.event.seat)
  }
  return out
}
